import { ChevronLeft, ChevronRight, Settings } from "lucide-react";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Loading } from "../../components";
import { Button } from "../../components/UI/Button";
import { auth } from "../../firebase";
import { useProductBase, useUserPartnerInformation } from "../../hooks";
import { ProductFilter } from "./ProductFilter";
import { ProductTableHead } from "./ProductTableHead";
import { TABLE_ITEMS } from "./constants";
import type { Product } from "../../types";

const TABLE_ROW_HEIGHT = 60;
const TABLE_HEAD_MARGIN = 12;

const Products = () => {
  function getNestedValue(obj: any, path: string): any {
    return path.split(".").reduce((acc, key) => acc?.[key], obj);
  }

  const [user] = useAuthState(auth);

  const { userPartnerInformation } = useUserPartnerInformation(user?.uid);

  const { data, isLoading } = useProductBase(
    userPartnerInformation?.data?.partnerId
  );

  const products: Product[] | undefined = data?.data;
  const location = useLocation();
  const navigate = useNavigate();

  const [filterProperty, setFilterProperty] = useState<string | null>(null);

  const [filterValue, setFilterValue] = useState<ReactNode | null>(null);

  const filterByValueOptions = useMemo(() => {
    if (!filterProperty) {
      return [];
    }

    const filterValues = products
      ?.map((item) => getNestedValue(item, filterProperty))
      .filter((item) => item && item !== "N/A");

    const uniqueFilterValues = [...new Set(filterValues)];
    return uniqueFilterValues.map((item) => ({
      label: String(item),
      onClick: () => setFilterValue(item as ReactNode),
    }));
  }, [products, filterProperty]);

  const [page, setPage] = useState(0);

  const filteredProducts = useMemo(() => {
    if (!filterProperty || !filterValue) {
      return products;
    }

    return products?.filter(
      (product) => getNestedValue(product, filterProperty) === filterValue
    );
  }, [filterProperty, filterValue, products]);

  const [tableHeight, setTableHeight] = useState(0);

  const tableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!tableRef?.current) {
      return;
    }

    setTableHeight(tableRef.current.clientHeight);
  }, [tableRef]);

  const rowsPerPage = useMemo(
    () =>
      Math.max(
        1,
        Math.floor(
          (tableHeight - TABLE_ROW_HEIGHT - TABLE_HEAD_MARGIN) /
          TABLE_ROW_HEIGHT
        )
      ),
    [tableHeight]
  );

  const totalPages = useMemo(
    () => Math.ceil((filteredProducts?.length || 0) / rowsPerPage),
    [filteredProducts, rowsPerPage]
  );

  const paginatedProducts = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return filteredProducts?.slice(start, end);
  }, [page, rowsPerPage, filteredProducts]);

  const handleNextPage = () => {
    if (page < totalPages - 1) {
      setPage((prevPage) => prevPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (page > 0) {
      setPage((prevPage) => prevPage - 1);
    }
  };

  // Column Customization Logic
  // const [visibleColumns, setVisibleColumns] = useState<{ label: string; property: string }[]>(TABLE_ITEMS);
  // const [showColumnSelector, setShowColumnSelector] = useState(false);

  // const getAllKeys = (data: Product[]) => {
  //   const keys = new Set<string>();
  //   const traverse = (obj: any, prefix = '') => {
  //     Object.keys(obj).forEach(key => {
  //       if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
  //         traverse(obj[key], prefix + key + '.');
  //       } else {
  //         keys.add(prefix + key);
  //       }
  //     });
  //   };
  //   // Sample first 10 items to discover schema
  //   data.slice(0, 10).forEach(item => traverse(item));
  //   return Array.from(keys);
  // };

  // const allAvailableColumns = useMemo(() => {
  //   if (!products || products.length === 0) return TABLE_ITEMS.map(i => i.property);
  //   return getAllKeys(products);
  // }, [products]);

  // const formatLabel = (key: string) => {
  //   return key
  //     .split('.')
  //     .map(part => part.replace(/([A-Z])/g, ' $1').trim())
  //     .map(part => part.charAt(0).toUpperCase() + part.slice(1))
  //     .join(' - ');
  // };

  // const toggleColumn = (property: string) => {
  //   setVisibleColumns(prev => {
  //     const exists = prev.find(p => p.property === property);
  //     if (exists) {
  //       return prev.filter(p => p.property !== property);
  //     } else {
  //       return [...prev, { label: formatLabel(property), property }];
  //     }
  //   });
  // };

  return (
    <>
      <div className="flex flex-col px-10 h-[calc(100vh-60px)] overflow-hidden">
        <div className="flex justify-between items-center mb-3 mt-12">
          <h1 className="text-xl font-bold me-2">Product Base</h1>
          <Link to={"/product-base/import"}>
            <Button>Import Sheet</Button>
          </Link>
        </div>
        <div className="flex w-full items-center justify-between gap-4">
          {(products ?? []).length > 0 && (
            <ProductFilter
              filterProperty={filterProperty as any}
              filterValue={filterValue}
              onFilterValueClick={(value) => setFilterValue(value)}
              // Cast to any to accept string if ProductFilter expects enum, 
              // assuming ProductFilter component might need update but skipping for now or simply casting property
              onFilterPropertyClick={(property: any) => setFilterProperty(property)}
              filterByValueOptions={filterByValueOptions}
            />
          )}

          <div className="flex items-center gap-4 ms-auto relative">
            {totalPages > 1 && (
              <div className="flex gap-2 items-center">
                <button
                  className="h-6 w-6 bg-gray-300 rounded-full flex items-center justify-center disabled:opacity-50"
                  disabled={page === 0}
                  onClick={handlePrevPage}
                >
                  <ChevronLeft className="h-4 w-4 stroke-slate-500" />
                </button>
                <p className="text-sm">
                  Page {page + 1} of {totalPages}
                </p>
                <button
                  className="h-6 w-6 bg-gray-300 rounded-full flex items-center justify-center disabled:opacity-50"
                  disabled={page + 1 === totalPages}
                  onClick={handleNextPage}
                >
                  <ChevronRight className="h-4 w-4 stroke-slate-500" />
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="flex-grow overflow-auto" ref={tableRef}>
          <div className="min-w-full inline-block align-middle pb-4">
            <div className="border rounded-lg overflow-hidden">
              <table className="leading-normal mt-3 w-full">
                <ProductTableHead visibleColumns={TABLE_ITEMS} />
                <tbody>
                  {paginatedProducts?.length ? (
                    paginatedProducts.map((item, index) => (
                      <tr
                        key={index}
                        role="row"
                        onClick={() => navigate(`/product/${item.sku}`, { state: { previousLocation: location } })}
                        className="cursor-pointer table-row bg-white border-b h-[60px] text-start align-middle border-gray-200 hover:bg-slate-50 transition-colors"
                      >
                        {TABLE_ITEMS.map(({ property }, i) => (
                          <td key={`${index}-${property}`} className="text-sm pl-5 py-3 whitespace-nowrap">
                            <p className="text-gray-900">
                              {/* Handle objects gracefully if strictly nested leaf wasn't selected */}
                              {typeof getNestedValue(item, property) === 'object'
                                ? JSON.stringify(getNestedValue(item, property))
                                : (getNestedValue(item, property) || "-")}
                            </p>
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={TABLE_ITEMS.length}
                        className="text-center py-4 text-sm text-gray-500"
                      >
                        No products found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {isLoading && <Loading floating />}
    </>
  );
};

export default Products;
